/**
 * React Query hooks for managing Audit History at the Deck and Page Levels
 * These hooks provide clean interfaces for fetching, caching, and managing Audit History using React Query.
 */

import useApiClients from '@hooks/useApiClients.tsx';
import {
  AuditHistory,
  GetAuditHistoryForDeckQueryVariables,
  GetAuditHistoryForPageQueryVariables,
} from '@services/api/deckManagement/graphql/schema/API';
import { getAuditHistoryForDeck, getAuditHistoryForPage } from '@services/api/deckManagement/graphql/schema/queries';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { extractGraphqlOperationName } from '@utils/graphqlOperationName';
import {
  createQueryOrMutationKey,
  createStandardInfiniteQueryOptions,
  createStandardQueryOptions,
  StandardMutationOptions,
  StandardQueryOptions,
} from '@utils/reactQueryConfig.ts';

/**
 * Interface for common audit history API parameters
 * @interface CommonAuditHistoryApiParams
 * @property {string} deckId - The unique identifier for the Deck
 * @property {StandardQueryOptions | StandardMutationOptions} options - Additional React Query options
 */
export interface CommonAuditHistoryApiParams {
  deckId?: string;
  options?: StandardQueryOptions | StandardMutationOptions;
}

// Query keys for Audit History related caching
export const auditHistoryQueryKeys = {
  all: createQueryOrMutationKey('auditHistory'),
  getAuditHistoryForDeck: (deckId: string) => createQueryOrMutationKey(...auditHistoryQueryKeys.all, 'deck', deckId),
  getAuditHistoryForPage: (deckId: string, pageId: string) =>
    createQueryOrMutationKey(...auditHistoryQueryKeys.all, 'deck', deckId, 'page', pageId),
};

/**
 * Hook to fetch Audit History for an entire Deck
 * @param deckId - The unique identifier for the Deck
 * @param options - Additional React Query options
 */
export const useGetAuditHistoryForDeck = ({
  deckId,
  options,
}: CommonAuditHistoryApiParams & Omit<GetAuditHistoryForDeckQueryVariables, 'deck_id'>) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: auditHistoryQueryKeys.getAuditHistoryForDeck(deckId || ''),
    queryFn: async (): Promise<AuditHistory[]> => {
      if (!deckId) {
        throw new Error('Deck ID is required');
      }

      // Retrieve Audit History for the Deck using the imported query
      const response = await deckManagementClient.graphql({
        query: getAuditHistoryForDeck,
        variables: {
          deck_id: deckId,
        },
      });

      if ('data' in response && response.data?.getAuditHistoryForDeck) {
        return response.data.getAuditHistoryForDeck as AuditHistory[];
      }

      throw new Error('Failed to fetch audit history for deck or deck not found');
    },
    ...createStandardQueryOptions<AuditHistory[]>({
      ...(options as StandardQueryOptions),
      enabled: Boolean(deckId) && ((options as StandardQueryOptions)?.enabled ?? true),
      rumOp: extractGraphqlOperationName(getAuditHistoryForDeck),
    }),
  });
};

/**
 * Hook to fetch Audit History for a specific Page
 * @param deckId - The unique identifier for the Deck
 * @param pageId - The unique identifier for the Page
 * @param limit - Optional limit for pagination
 * @param options - Additional React Query options
 */
export const useGetAuditHistoryForPage = ({
  deckId,
  page_id: pageId,
  limit = 50,
  options,
}: CommonAuditHistoryApiParams & Omit<GetAuditHistoryForPageQueryVariables, 'deck_id'>) => {
  const { deckManagementClient } = useApiClients();

  return useInfiniteQuery({
    queryKey: auditHistoryQueryKeys.getAuditHistoryForPage(deckId || '', pageId || ''),
    queryFn: async ({
      pageParam,
    }: {
      pageParam: string | undefined;
    }): Promise<{ items: AuditHistory[]; nextToken?: string }> => {
      if (!deckId) {
        throw new Error('Deck ID is required');
      }

      if (!pageId) {
        throw new Error('Page ID is required');
      }

      // Retrieve Audit History for the Page using the imported query
      const response = await deckManagementClient.graphql({
        query: getAuditHistoryForPage,
        variables: {
          deck_id: deckId,
          page_id: pageId,
          limit,
          nextToken: pageParam,
        },
      });

      if ('data' in response && response.data?.getAuditHistoryForPage) {
        const auditItems = response.data.getAuditHistoryForPage.items;
        return {
          items: auditItems?.filter((item): item is AuditHistory => item !== null) ?? [],
          nextToken: response.data.getAuditHistoryForPage.nextToken ?? undefined,
        };
      }

      throw new Error('Failed to fetch audit history for page or page not found');
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: { items: AuditHistory[]; nextToken?: string }) => lastPage.nextToken ?? undefined,
    ...createStandardInfiniteQueryOptions({
      ...(options as StandardQueryOptions),
      enabled: Boolean(deckId) && Boolean(pageId) && ((options as StandardQueryOptions)?.enabled ?? true),
      rumOp: extractGraphqlOperationName(getAuditHistoryForPage),
    }),
  });
};
